import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Badge,
  Text,
  Button,
  VStack,
  HStack,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import type { CleanupAction, CleanupTask } from './control-panel-dummy-data';
import { DUMMY_ACTIONS, DUMMY_TASKS } from './control-panel-dummy-data';
import { ActionsDetailModal } from './actions-detail-modal';

const STATUS_DOT_COLOR: Record<CleanupTask['status'], string> = {
  running: 'blue.500',
  pending: 'gray.400',
  done: 'green.500',
  error: 'red.500',
};

const TYPE_BADGE_COLOR: Record<CleanupAction['type'], string> = {
  cleanup: 'teal',
  optimization: 'purple',
};

const TYPE_LABEL: Record<CleanupAction['type'], string> = {
  cleanup: 'Clean up',
  optimization: 'Optimization',
};

const taskStatusMap = DUMMY_TASKS.reduce<Record<string, CleanupTask['status']>>(
  (acc, task) => { acc[task.id] = task.status; return acc; },
  {}
);

interface ActionsSectionProps {
  onActionsChange?: (count: number) => void;
}

export const ActionsSection: React.FC<ActionsSectionProps> = ({ onActionsChange }) => {
  const [actions, setActions] = useState<CleanupAction[]>(DUMMY_ACTIONS);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleRemoveAction = (id: string) => {
    const updated = actions.filter((a) => a.id !== id);
    setActions(updated);
    onActionsChange?.(updated.length);
  };

  const preview = actions.slice(0, 3);

  return (
    <>
      <Box>
        <Flex justify="space-between" align="center" mb={3}>
          <Heading size="sm" color="gray.700">
            Actions
          </Heading>
          <Badge colorScheme="purple" borderRadius="full" px={2}>
            {actions.length}
          </Badge>
        </Flex>

        <VStack align="stretch" spacing={2}>
          {preview.map((action) => (
            <HStack key={action.id} spacing={2} align="center">
              <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg={STATUS_DOT_COLOR[taskStatusMap[action.taskId] ?? 'pending']}
                flexShrink={0}
              />
              <Text fontSize="xs" color="gray.700" flex={1} noOfLines={1}>
                {action.name}
              </Text>
              <Badge colorScheme={TYPE_BADGE_COLOR[action.type]} fontSize="2xs" flexShrink={0}>
                {TYPE_LABEL[action.type]}
              </Badge>
            </HStack>
          ))}
        </VStack>

        {actions.length > 0 && (
          <Button
            variant="link"
            colorScheme="blue"
            size="xs"
            mt={2}
            onClick={onOpen}
            fontWeight="normal"
          >
            View all &amp; manage ({actions.length})
          </Button>
        )}

        {actions.length === 0 && (
          <Text fontSize="xs" color="gray.400" mt={1}>
            No actions selected.
          </Text>
        )}
      </Box>

      <ActionsDetailModal
        isOpen={isOpen}
        onClose={onClose}
        actions={actions}
        onRemoveAction={handleRemoveAction}
      />
    </>
  );
};
